<template>
  <div class="rewards-page">
    <!-- 상단: 타이틀 + 탭 -->
    <div class="head">
      <div class="title-row">
        <h2>연속 보상</h2>
        <button
          class="refresh-btn"
          type="button"
          :disabled="loading"
          @click="reload"
        >
          {{ loading ? "불러오는 중…" : "새로고침" }}
        </button>
      </div>
      <p class="desc">
        주간 미션을 연속으로 성공한 회원의 보상이에요. 회원의 수강권을 골라
        적용하면 그 수강권만 바뀌고 회원 앱에 보상 팝업이 떠요.
      </p>

      <div class="filters" role="tablist">
        <button
          type="button"
          role="tab"
          :aria-selected="tab === 'pending'"
          :class="['filter-btn', { active: tab === 'pending' }]"
          @click="setTab('pending')"
        >
          지급 대기<span v-if="loaded.pending" class="count">{{
            pendingCount
          }}</span>
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="tab === 'applied'"
          :class="['filter-btn', { active: tab === 'applied' }]"
          @click="setTab('applied')"
        >
          적용 완료
        </button>
      </div>

      <div v-if="userIdFilter" class="user-filter">
        <span>회원 한 명의 보상만 보는 중</span>
        <button type="button" class="link-btn" @click="clearUserFilter">
          전체 보기
        </button>
      </div>
    </div>

    <!-- 적용 결과·안내 -->
    <p
      v-if="notice"
      :class="['notice', `notice--${notice.kind}`]"
      role="status"
      data-test="notice"
    >
      {{ notice.message }}
    </p>

    <!-- 목록 -->
    <div class="list">
      <p v-if="errors[tab]" class="empty error" role="alert">
        {{ errors[tab] }}
        <button type="button" class="link-btn" @click="reload">
          다시 불러오기
        </button>
      </p>
      <p v-else-if="loading && !loaded[tab]" class="empty">불러오는 중…</p>
      <p v-else-if="loaded[tab] && currentItems.length === 0" class="empty">
        {{
          tab === "pending"
            ? "지급 대기 중인 보상이 없어요."
            : "적용한 보상이 없어요."
        }}
      </p>

      <!-- 지급 대기: 오래된 순 -->
      <template v-if="tab === 'pending'">
        <div
          v-for="item in currentItems"
          :key="item.id"
          class="reward-row"
          data-test="pending-row"
          :data-reward-id="item.id"
        >
          <div class="left">
            <div class="name-line">
              <router-link class="name" :to="profileLink(item)">{{
                item.userName || "이름 없음"
              }}</router-link>
              <span class="tier-badge">{{ item.streakWeeks }}주 연속</span>
            </div>
            <div class="meta">
              <span class="meta-item">
                <span class="label">달성일</span>
                <span class="value">{{ fullDate(achievedDate(item)) }}</span>
              </span>
              <span class="meta-item">
                <span class="label">미션 주</span>
                <span class="value">{{ missionWeek(item.weekStart) }}</span>
              </span>
              <span class="meta-item">
                <span class="label">대기</span>
                <span class="value">{{ waiting(item.createdAt) }}</span>
              </span>
            </div>
            <p v-if="!hasMemberships(item)" class="guide" data-test="no-pass">
              활성 수강권이 없어요. 수강권을 먼저 등록해 주세요.
              <router-link class="guide-link" :to="profileLink(item)"
                >회원 상세로 가기</router-link
              >
            </p>
          </div>
          <div class="right">
            <button
              type="button"
              class="apply-btn"
              :disabled="applying"
              @click="openApply(item)"
            >
              적용
            </button>
          </div>
        </div>
        <p
          v-if="loaded.pending && lists.pending.length >= limits.pending"
          class="hint"
        >
          오래된 순서로 {{ limits.pending }}건까지 보여요. 적용하면 다음 대기가
          이어서 보여요.
        </p>
      </template>

      <!-- 적용 완료: 최신 순 -->
      <template v-else>
        <div
          v-for="item in currentItems"
          :key="item.id"
          class="reward-row reward-row--applied"
          data-test="applied-row"
          :data-reward-id="item.id"
        >
          <div class="left">
            <div class="name-line">
              <router-link class="name" :to="profileLink(item)">{{
                item.userName || "이름 없음"
              }}</router-link>
              <span class="tier-badge">{{ item.streakWeeks }}주 연속</span>
            </div>
            <div class="meta">
              <span class="meta-item">
                <span class="label">달성일</span>
                <span class="value">{{ fullDate(achievedDate(item)) }}</span>
              </span>
              <span class="meta-item">
                <span class="label">적용 일시</span>
                <span class="value">{{
                  dateTime(item.applied && item.applied.appliedAt)
                }}</span>
              </span>
              <span class="meta-item">
                <span class="label">적용한 관리자</span>
                <span class="value">{{ adminName(item) }}</span>
              </span>
            </div>
          </div>
          <div class="right applied-values">
            <div class="applied-head">
              <span class="mem-type">{{ appliedMembership(item) }}</span>
              <span class="value-chip">{{ appliedValue(item) }}</span>
            </div>
            <span class="before-after" data-test="before-after">{{
              beforeAfter(item)
            }}</span>
          </div>
        </div>
        <p
          v-if="loaded.applied && lists.applied.length >= limits.applied"
          class="hint"
        >
          최근 {{ limits.applied }}건까지 보여요.
        </p>
      </template>
    </div>

    <!-- 적용 모달 -->
    <div v-if="modal" class="modal-overlay" @click="onOverlayClick">
      <div
        class="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reward-apply-title"
        data-test="apply-modal"
        @click.stop
      >
        <h3 id="reward-apply-title" class="modal-title">
          {{ modal.item.userName || "이름 없음" }} 회원 ·
          {{ modal.item.streakWeeks }}주 연속 보상
        </h3>
        <p class="modal-sub">
          달성일 {{ fullDate(achievedDate(modal.item)) }} · 미션 주
          {{ missionWeek(modal.item.weekStart) }}
        </p>

        <template v-if="modalMemberships.length">
          <p class="field-label" id="reward-membership-label">적용할 수강권</p>
          <div
            class="mem-options"
            role="radiogroup"
            aria-labelledby="reward-membership-label"
          >
            <label
              v-for="m in modalMemberships"
              :key="m.id"
              :class="['mem-option', { selected: m.id === modal.choice }]"
              data-test="membership-option"
            >
              <input
                v-model="modal.choice"
                type="radio"
                name="reward-membership"
                :value="m.id"
                :disabled="applying || modal.stale"
                @change="modal.error = ''"
              />
              <span class="mem-option-body">
                <span class="mem-option-head">
                  <span class="mem-type">{{ m.type || "수강권" }}</span>
                  <span
                    v-if="m.id === modal.item.suggestedMembershipId"
                    class="suggest-badge"
                    >추천</span
                  >
                </span>
                <span class="mem-option-detail">{{ summary(m, refYear) }}</span>
              </span>
              <span class="mem-option-value">{{ optionValue(m) }}</span>
            </label>
          </div>
          <p class="suggest-hint">
            추천은 만료가 가장 늦은 수강권이에요. 다른 수강권을 골라도 돼요.
          </p>

          <div
            v-if="!modal.stale"
            class="preview"
            aria-live="polite"
            data-test="preview"
          >
            <span class="preview-label">적용하면</span>
            <span class="preview-value">{{ previewLine }}</span>
          </div>
        </template>
        <div v-else class="guide guide--modal" data-test="modal-no-pass">
          <p>활성 수강권이 없어요. 수강권을 먼저 등록해 주세요.</p>
          <router-link class="guide-link" :to="profileLink(modal.item)"
            >회원 상세로 가기</router-link
          >
        </div>

        <p v-if="modal.error" class="modal-error" role="alert">
          {{ modal.error }}
        </p>

        <div class="modal-actions">
          <button
            type="button"
            class="btn"
            :disabled="applying"
            @click="closeApply"
          >
            {{ modalMemberships.length ? "취소" : "닫기" }}
          </button>
          <button
            v-if="modalMemberships.length && !modal.stale"
            type="button"
            class="btn btn-primary"
            data-test="confirm-apply"
            :disabled="applying || !modal.choice"
            @click="confirmApply"
          >
            {{ applying ? "적용 중…" : "적용" }}
          </button>
          <!-- 422 뒤 목록 다시 받기가 실패한 모달: 새 목록을 받기 전에는 적용하지 않는다 -->
          <button
            v-else-if="modal.stale"
            type="button"
            class="btn btn-primary"
            data-test="modal-reload"
            :disabled="modal.reloading"
            @click="reloadModal"
          >
            {{ modal.reloading ? "불러오는 중…" : "목록 다시 불러오기" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// R4 연속 보상 적용 화면(결정 R4-5). 정산 배치는 지급 대기만 만들고, 수강권은 여기서
// 관리자가 고른 것에만 +횟수/+기간이 적용된다. 계약: docs/architecture/app-v2/R6a-R4.md B2·B3
import {
  listRewards,
  applyReward,
  describeApplyError,
  describeListError,
  reloadResultText,
  REWARD_LIST_LIMIT_MAX,
} from "@/lib/rewardsApi";
import {
  appliedMembershipName,
  beforeAfterText,
  formatDateTime,
  formatFullDate,
  formatMissionWeek,
  initialMembershipId,
  membershipSummary,
  orderMemberships,
  previewText,
  rewardTypeOf,
  rewardValueText,
  seoulYear,
  sortApplied,
  sortPending,
  waitingText,
} from "@/lib/rewardFormat";

// 주소의 tab 쿼리를 탭 이름으로 바꾼다(applied 말고는 지급 대기)
function tabFromQuery(value) {
  return value === "applied" ? "applied" : "pending";
}

export default {
  name: "RewardsView",
  data() {
    return {
      tab: tabFromQuery(this.$route.query.tab),
      lists: { pending: [], applied: [] },
      loaded: { pending: false, applied: false },
      errors: { pending: "", applied: "" },
      limits: { pending: REWARD_LIST_LIMIT_MAX, applied: 100 },
      loadingCount: 0,
      // 탭마다 마지막 요청 번호. 늦게 온 이전 응답은 버린다(회원 필터가 바뀌면 두 탭 모두 올려 이전 필터의 응답도 버린다)
      requestSeq: { pending: 0, applied: 0 },
      // 이번 화면에서 적용(또는 409로 이미 적용 확인)한 보상. 목록을 다시 받기 전에도 숨겨 두 번 누르지 않게
      doneIds: {},
      notice: null,
      modal: null,
      applying: false,
      refYear: seoulYear(),
    };
  },
  computed: {
    loading() {
      return this.loadingCount > 0;
    },
    userIdFilter() {
      const v = this.$route.query.userId;
      return typeof v === "string" ? v : "";
    },
    pendingItems() {
      return this.lists.pending.filter((item) => !this.doneIds[item.id]);
    },
    pendingCount() {
      return this.pendingItems.length;
    },
    currentItems() {
      return this.tab === "pending" ? this.pendingItems : this.lists.applied;
    },
    modalMemberships() {
      if (!this.modal) return [];
      return orderMemberships(
        this.modal.item.memberships,
        this.modal.item.suggestedMembershipId
      );
    },
    selectedMembership() {
      if (!this.modal) return null;
      return (
        this.modalMemberships.find((m) => m.id === this.modal.choice) || null
      );
    },
    previewLine() {
      return previewText(this.selectedMembership, this.refYear);
    },
  },
  watch: {
    "$route.query.userId"() {
      // 다른 화면으로 나갈 때 바뀐 쿼리에는 반응하지 않는다
      if (this.$route.name !== "Rewards") return;
      // 이전 필터로 보낸 요청은 지금 탭뿐 아니라 다른 탭 것도 버린다. 늦게 온 응답이 다른 탭을 채우고 loaded로 표시하면
      // 그 탭을 열 때 다시 받지 않아 필터 없이 한 회원의 목록만 보인다
      this.requestSeq = {
        pending: this.requestSeq.pending + 1,
        applied: this.requestSeq.applied + 1,
      };
      this.loaded = { pending: false, applied: false };
      this.lists = { pending: [], applied: [] };
      this.errors = { pending: "", applied: "" };
      this.loadList(this.tab);
    },
    "$route.query.tab"(value) {
      if (this.$route.name !== "Rewards") return;
      // 사이드 메뉴 "연속 보상"(/rewards)·뒤로 가기처럼 주소만 바뀐 경우에도 탭을 주소에 맞춘다(setTab이 바꾼 주소는 이미 같음)
      const next = tabFromQuery(value);
      if (next === this.tab) return;
      this.tab = next;
      if (!this.loaded[next]) this.loadList(next);
    },
  },
  created() {
    this.loadList(this.tab);
  },
  methods: {
    // 목록을 받는다. 이번 응답으로 화면 목록을 바꿨으면 true, 실패했거나 늦게 와서 버렸으면 false
    // (false면 화면 목록은 예전 값 그대로다. 적용 실패 뒤 "새로 불러왔어요"는 true일 때만 쓴다)
    async loadList(which = this.tab) {
      const seq = ++this.requestSeq[which];
      // 요청한 회원 필터. 응답이 왔을 때 필터가 바뀌었으면 버린다(요청 번호와 함께 이중으로 막음)
      const userId = this.userIdFilter;
      this.loadingCount++;
      try {
        const items = await listRewards({
          status: which,
          userId,
          limit: this.limits[which],
        });
        if (seq !== this.requestSeq[which] || userId !== this.userIdFilter)
          return false;
        this.lists[which] =
          which === "pending" ? sortPending(items) : sortApplied(items);
        this.loaded[which] = true;
        this.errors[which] = "";
        if (which === "pending") {
          // 다시 받은 대기 목록에 없는 보상은 숨김 표시가 더 필요 없다
          const ids = new Set(items.map((item) => item.id));
          const nextDone = {};
          Object.keys(this.doneIds).forEach((id) => {
            if (ids.has(id)) nextDone[id] = true;
          });
          this.doneIds = nextDone;
        }
        return true;
      } catch (err) {
        if (seq !== this.requestSeq[which] || userId !== this.userIdFilter)
          return false;
        this.errors[which] = describeListError(err);
        return false;
      } finally {
        this.loadingCount--;
      }
    },
    reload() {
      this.refYear = seoulYear();
      this.loadList(this.tab);
    },
    // 탭 목록이 낡았다고 표시한다(적용으로 적용 완료 목록이 바뀜). 요청 번호도 올려, 적용 전에 보내 아직 오고 있는 그 탭 응답이
    // 늦게 와서 적용 전 목록과 loaded를 다시 채우지 못하게 한다(그러면 탭을 열 때 다시 받지 않아 방금 적용한 보상이 빠진다).
    // 그 탭을 보고 있으면 바로 다시 받는다
    invalidateList(which) {
      this.requestSeq[which]++;
      this.loaded[which] = false;
      if (this.tab === which) this.loadList(which);
    },
    setTab(next) {
      if (this.tab === next) return;
      this.tab = next;
      const query = { ...this.$route.query };
      if (next === "applied") query.tab = "applied";
      else delete query.tab;
      this.$router.replace({ query }).catch(() => {});
      if (!this.loaded[next]) this.loadList(next);
    },
    clearUserFilter() {
      const query = { ...this.$route.query };
      delete query.userId;
      this.$router.replace({ query }).catch(() => {});
    },
    hasMemberships(item) {
      return Array.isArray(item?.memberships) && item.memberships.length > 0;
    },
    profileLink(item) {
      return `/profile/${encodeURIComponent(item?.userId || "")}`;
    },
    achievedDate(item) {
      return item?.achievedOn || item?.weekStart || "";
    },
    fullDate(value) {
      return formatFullDate(value);
    },
    missionWeek(weekStart) {
      return formatMissionWeek(weekStart);
    },
    waiting(createdAt) {
      return waitingText(createdAt);
    },
    dateTime(value) {
      return formatDateTime(value);
    },
    summary(membership, refYear) {
      return membershipSummary(membership, refYear);
    },
    optionValue(membership) {
      return rewardValueText(
        rewardTypeOf(membership),
        membership?.preview?.rewardValue
      );
    },
    adminName(item) {
      const by = item?.applied?.appliedBy;
      return (by && by.name) || "-";
    },
    appliedMembership(item) {
      return appliedMembershipName(item);
    },
    appliedValue(item) {
      const applied = item?.applied;
      if (!applied) return "-";
      return rewardValueText(applied.rewardType, applied.rewardValue);
    },
    beforeAfter(item) {
      return beforeAfterText(item?.applied, this.refYear);
    },
    openApply(item) {
      if (this.applying || this.doneIds[item.id]) return;
      this.refYear = seoulYear();
      this.modal = {
        item,
        choice: initialMembershipId(item),
        error: "",
        // 422 뒤 목록 다시 받기가 실패해 수강권 목록이 낡았음(적용 대신 [목록 다시 불러오기])
        stale: false,
        reloading: false,
      };
    },
    onOverlayClick() {
      // 적용 요청 중에는 닫지 않는다(결과를 모달에서 보여 준다)
      if (!this.applying) this.closeApply();
    },
    closeApply() {
      if (this.applying) return;
      this.modal = null;
    },
    // 목록을 다시 받은 뒤 열린 모달의 보상을 새 값으로 바꾼다. 대기 목록에서 사라졌으면 false
    syncModalItem() {
      if (!this.modal) return false;
      const fresh = this.lists.pending.find(
        (item) => item.id === this.modal.item.id
      );
      if (!fresh) return false;
      const keep = (fresh.memberships || []).some(
        (m) => m && m.id === this.modal.choice
      );
      this.modal.item = fresh;
      if (!keep) this.modal.choice = initialMembershipId(fresh);
      return true;
    },
    // 다시 받은 대기 목록에 없는 보상의 모달을 닫는다(네트워크 오류 뒤 서버는 처리했거나 다른 관리자가 적용한 경우)
    closeGoneModal() {
      this.modal = null;
      this.notice = {
        kind: "info",
        message:
          "이 보상은 더 이상 지급 대기에 없어요. 적용 완료 탭에서 결과를 확인해 주세요.",
      };
      this.invalidateList("applied");
    },
    // 낡은 모달(422 뒤 목록 다시 받기 실패)에서 목록을 다시 받는다. 받으면 모달의 보상·수강권을 새 값으로 바꾸고
    // [적용]을 돌려준다. 또 실패하면 낡은 상태 그대로 둔다(이유는 목록의 오류 줄)
    async reloadModal() {
      const open = this.modal;
      if (!open || !open.stale || open.reloading || this.applying) return;
      open.reloading = true;
      this.refYear = seoulYear();
      const reloaded = await this.loadList("pending");
      open.reloading = false;
      if (this.modal !== open || !reloaded) return;
      if (this.syncModalItem()) {
        open.stale = false;
        open.error = reloadResultText("invalid", true);
      } else {
        this.closeGoneModal();
      }
    },
    appliedNotice(reward, result) {
      const applied = result?.applied;
      const name = reward.userName || "이름 없음";
      if (!applied) {
        return `${name} 회원의 ${reward.streakWeeks}주 연속 보상을 적용했어요.`;
      }
      // 수강권 이름: 응답에 없으면 모달에서 고른 수강권(적용 전 목록)에서 찾는다
      const membership = appliedMembershipName({
        ...result,
        memberships: [
          ...(Array.isArray(result.memberships) ? result.memberships : []),
          ...(Array.isArray(reward.memberships) ? reward.memberships : []),
        ],
      });
      const value = rewardValueText(applied.rewardType, applied.rewardValue);
      const change = beforeAfterText(applied, this.refYear);
      return `${name} 회원 ${membership}에 ${
        reward.streakWeeks
      }주 연속 보상(${value})을 적용했어요${
        change !== "-" ? ` (${change})` : ""
      }.`;
    },
    async confirmApply() {
      // 두 번 누름: 첫 요청이 끝날 때까지 버튼을 막고, 여기서도 한 번 더 막는다
      if (this.applying || !this.modal || !this.modal.choice) return;
      if (this.modal.stale) return; // 낡은 수강권 목록으로는 적용하지 않는다
      const reward = this.modal.item;
      if (this.doneIds[reward.id]) return;
      const membershipId = this.modal.choice;
      this.applying = true;
      this.modal.error = "";
      let reloadPending = false;
      let failure = null;
      try {
        const result = await applyReward(reward.id, membershipId);
        this.doneIds = { ...this.doneIds, [reward.id]: true };
        this.notice = {
          kind: "success",
          message: this.appliedNotice(reward, result),
        };
        this.modal = null;
        this.invalidateList("applied");
        reloadPending = true;
      } catch (err) {
        failure = describeApplyError(err);
        if (failure.kind === "already") {
          this.doneIds = { ...this.doneIds, [reward.id]: true };
          this.notice = { kind: "info", message: failure.message };
          this.modal = null;
          this.invalidateList("applied");
        } else if (failure.kind === "missing") {
          this.notice = { kind: "error", message: failure.message };
          this.modal = null;
        } else {
          this.modal.error = failure.message;
        }
        // 422·네트워크 오류는 수강권·적용 여부가 바뀌었을 수 있어 목록도 새로 받는다
        reloadPending = ["already", "missing", "invalid", "network"].includes(
          failure.kind
        );
      } finally {
        this.applying = false;
      }
      if (!reloadPending) return;
      // 실패해도 열려 있는 모달(422·네트워크 오류). 새로 받는 동안 다른 모달로 바뀌면 건드리지 않는다
      const stillOpen = failure ? this.modal : null;
      const missingNotice = failure?.kind === "missing" ? this.notice : null;
      const reloaded = await this.loadList("pending");
      if (missingNotice && this.notice === missingNotice) {
        // 404 안내 뒤에 다시 받은 결과를 붙인다(실패했으면 "새로 불러왔어요"라고 하지 않음)
        this.notice = {
          kind: "error",
          message: `${failure.message} ${reloadResultText(
            "missing",
            reloaded
          )}`,
        };
      }
      if (!stillOpen || this.modal !== stillOpen) return;
      if (!reloaded) {
        // 다시 받기가 실패했다(또는 늦게 와서 버렸다). 화면 목록은 예전 값이라 모달을 그것으로 맞추지 않는다.
        // 422면 방금 거절된 수강권이 그대로 남으므로 낡은 모달로 바꿔 적용 대신 [목록 다시 불러오기]를 보인다.
        // 네트워크 오류면 그대로 두어 다시 시도하게 한다(이미 처리됐으면 서버가 409로 답함)
        if (failure.kind === "invalid") {
          this.modal.stale = true;
          this.modal.error = `${failure.message}\n${reloadResultText(
            "invalid",
            false
          )}`;
        }
        return;
      }
      if (this.syncModalItem()) {
        if (failure.kind === "invalid") {
          this.modal.error = `${failure.message}\n${reloadResultText(
            "invalid",
            true
          )}`;
        }
      } else {
        // 다시 받은 대기 목록에 없으면 이미 적용된 것(네트워크 오류 뒤 서버는 처리한 경우)
        this.closeGoneModal();
      }
    },
  },
};
</script>

<style scoped>
.rewards-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #151718;
  border-radius: 10px;
  padding: 16px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.head {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.head h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  color: #fff;
}

.desc {
  margin: 0;
  color: #8e8e8e;
  font-size: 14px;
  line-height: 1.5;
}

.refresh-btn {
  height: 36px;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.refresh-btn:hover:not(:disabled) {
  background: #252728;
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 탭 (무료체험 신청내역 필터와 같은 모양) */
.filters {
  display: flex;
  gap: 8px;
}

.filter-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 6px;
  background: #1b1d1e;
  color: #9d9d9d;
  border: 1px solid #333;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.filter-btn:hover:not(.active) {
  background: #252728;
}

.filter-btn.active {
  background: #78ff47;
  color: #000;
  border-color: #78ff47;
}

.filter-btn .count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.15);
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}

.filter-btn:not(.active) .count {
  background: #333;
  color: #fff;
}

.user-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #c8c8c8;
  font-size: 14px;
}

.link-btn {
  background: none;
  border: 0;
  padding: 0;
  color: #78ff47;
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
}

/* 안내 줄 */
.notice {
  margin: 0;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  white-space: pre-wrap;
}

.notice--success {
  border-color: rgba(120, 255, 71, 0.5);
  background: rgba(120, 255, 71, 0.08);
}

.notice--info {
  border-color: rgba(74, 144, 226, 0.6);
  background: rgba(74, 144, 226, 0.1);
}

.notice--error {
  border-color: rgba(255, 70, 71, 0.6);
  background: rgba(255, 70, 71, 0.1);
}

/* 목록 */
.list {
  display: grid;
  gap: 12px;
  min-width: 0;
}

.empty {
  margin: 0;
  color: #c8c8c8;
  padding: 12px;
}

.empty.error {
  color: #ff8a8b;
}

.hint {
  margin: 0;
  color: #8e8e8e;
  font-size: 13px;
}

.reward-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  background: #1b1d1e;
  border: 1px solid #333;
  border-radius: 8px;
  min-width: 0;
}

.left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.name-line {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.name {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
}

.name:hover {
  text-decoration: underline;
}

.tier-badge {
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(120, 255, 71, 0.12);
  color: #78ff47;
  font-size: 12px;
  font-weight: 600;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  font-size: 13px;
}

.meta-item {
  display: inline-flex;
  gap: 6px;
}

.meta-item .label {
  color: #8e8e8e;
  font-size: 13px;
}

.meta-item .value {
  color: #fff;
}

.guide {
  margin: 0;
  color: #ffb547;
  font-size: 13px;
  line-height: 1.5;
}

.guide-link {
  color: #78ff47;
  margin-left: 6px;
}

.right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  min-width: 0;
}

.apply-btn {
  height: 40px;
  min-width: 88px;
  padding: 0 16px;
  border-radius: 6px;
  border: 0;
  background: #78ff47;
  color: #000;
  font-weight: 600;
  cursor: pointer;
}

.apply-btn:hover:not(:disabled) {
  background: #6bff3a;
}

.apply-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.applied-values {
  text-align: right;
}

.applied-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mem-type {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
}

.value-chip {
  padding: 2px 8px;
  border-radius: 4px;
  background: #78ff47;
  color: #000;
  font-size: 12px;
  font-weight: 700;
}

.before-after {
  color: #c8c8c8;
  font-size: 13px;
}

/* 적용 모달 (회원 정보 화면 모달과 같은 모양) */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.modal {
  background: #0a0a0a;
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  width: 480px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  padding: 20px;
  box-sizing: border-box;
  color: #fff;
}

.modal-title {
  color: #fff;
  margin: 0 0 6px 0;
  font-size: 18px;
}

.modal-sub {
  margin: 0 0 16px 0;
  color: #8e8e8e;
  font-size: 13px;
  line-height: 1.5;
}

.field-label {
  margin: 0 0 8px 0;
  color: #8e8e8e;
  font-size: 14px;
}

.mem-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mem-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #151718;
  cursor: pointer;
}

.mem-option.selected {
  border-color: #78ff47;
  box-shadow: 0 0 0 1px rgba(120, 255, 71, 0.3);
}

.mem-option input {
  accent-color: #78ff47;
  margin: 0;
}

.mem-option-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.mem-option-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.suggest-badge {
  padding: 1px 6px;
  border-radius: 4px;
  background: #78ff47;
  color: #000;
  font-size: 11px;
  font-weight: 700;
}

.mem-option-detail {
  color: #8e8e8e;
  font-size: 13px;
}

.mem-option-value {
  color: #78ff47;
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
}

.suggest-hint {
  margin: 8px 0 0 0;
  color: #8e8e8e;
  font-size: 12px;
}

.preview {
  margin-top: 14px;
  padding: 12px;
  border-radius: 8px;
  background: #1b1d1e;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-label {
  color: #8e8e8e;
  font-size: 12px;
}

.preview-value {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.guide--modal {
  padding: 12px;
  border-radius: 8px;
  background: #1b1d1e;
}

.guide--modal p {
  margin: 0 0 6px 0;
}

.guide--modal .guide-link {
  margin-left: 0;
}

.modal-error {
  margin: 12px 0 0 0;
  color: #ff8a8b;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-line;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.btn {
  background: #333;
  color: #fff;
  border: 1px solid #555;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
}

.btn-primary {
  background: #78ff47;
  color: #000;
  border: 0;
  font-weight: 600;
  min-width: 88px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 반응형 */
@media (max-width: 720px) {
  .head h2 {
    font-size: 20px;
  }

  .filter-btn {
    flex: 1;
    justify-content: center;
  }

  .reward-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .right {
    align-items: stretch;
  }

  .applied-values {
    text-align: left;
  }

  .apply-btn {
    width: 100%;
  }
}
</style>
